import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, FastField, FieldArray } from "formik";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import { FormHelperText, Grid, Tooltip, Typography } from "@material-ui/core";
import NumberFormat from "react-number-format";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectDaysModal: {
    height: '24px',
    width: '24px',
    borderRadius: '50%',
    border: '1px solid black',
    fontSize: '10px',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '8px',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    cursor: 'pointer'
  }
}));


const initialValues = {
  userId: '',
  phone: '',
  interval: 1,
  days: [
    { weekday: i18n.t("plantao.modal.weekDays.monday"), weekdayEn: "Monday", startTime: null, endTime: null, },
    { weekday: i18n.t("plantao.modal.weekDays.tuesday"), weekdayEn: "Tuesday", startTime: null, endTime: null, },
    { weekday: i18n.t("plantao.modal.weekDays.wednesday"), weekdayEn: "Wednesday", startTime: null, endTime: null, },
    { weekday: i18n.t("plantao.modal.weekDays.thursday"), weekdayEn: "Thursday", startTime: null, endTime: null, },
    { weekday: i18n.t("plantao.modal.weekDays.friday"), weekdayEn: "Friday", startTime: null, endTime: null },
    { weekday: i18n.t("plantao.modal.weekDays.saturday"), weekdayEn: "Saturday", startTime: null, endTime: null },
    { weekday: i18n.t("plantao.modal.weekDays.sunday"), weekdayEn: "Sunday", startTime: null, endTime: null },
  ]
};


const validationSchema = Yup.object().shape({
  userId: Yup.string().required(i18n.t('plantao.modal.validation.user')),
  phone: Yup.string().required(i18n.t('plantao.modal.validation.phone')),
  interval: Yup.number().required(i18n.t('plantao.modal.validation.interval')),

  days: Yup.array()
    .of(
      Yup.object().shape({
        startTime: Yup.string()
          .nullable()
          .test(
            'startTime-required-if-endTime',
            i18n.t('plantao.modal.validation.startOnCall'),
            function (value) {
              const { endTime } = this.parent;
              if (endTime) {
                return !!value;
              }
              return true;
            }
          ),
        endTime: Yup.string()
          .nullable()
          .test(
            'endTime-required-if-startTime',
            i18n.t('plantao.modal.validation.endOnCall'),
            function (value) {
              const { startTime } = this.parent;
              if (startTime) {
                return !!value;
              }
              return true;
            }
          ),
      })
    )
    .test(
      'at-least-one-day-filled',
      i18n.t('plantao.modal.validation.atLeastOneDay'),
      function (days) {
        return days.some(day => day.startTime && day.endTime);
      }
    ),
});

export const ModalPlantao = ({ open, onClose, plantaoId, callback }) => {

  const classes = useStyles();
  const [plantao, setPlantao] = useState(initialValues);

  useEffect(() => {

    if (plantaoId) {
      handleGetPlantaoDetails();
    }
  }, [plantaoId]);


  const handleClose = () => {
    onClose();
    setPlantao(initialValues);
  };

  const handleSave = async (values) => {

    try {
      const { data } = await api.post('/plantao', values);
      handleClose();
      callback();
    } catch (error) {
      console.log('erro ao salvar plantonista', error);
      toastError(error);
    }

  }

  const handleGetPlantaoDetails = async () => {
    try {
      const { data } = await api.get(`/plantao/${plantaoId}`);
      setPlantao(data);
    } catch (error) {
      console.log('erro ao buscar plantonista', error);
    }
  }

  const handleUpdatePlantao = async (values) => {
    try {
      const { data } = await api.put(`/plantao/${plantaoId}`, values);
      handleClose();
      callback();
    } catch (error) {
      console.log('erro ao atualizar plantonista', error);
    }
  }

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {plantaoId
            ? i18n.t('plantao.modal.title.edit')
            : i18n.t('plantao.modal.title.add')}
        </DialogTitle>
        <Formik
          initialValues={plantao}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              plantaoId
                ? handleUpdatePlantao(values)
                : handleSave(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={1}>

                  <Grid item xs={4}>
                    <UsersContainer
                      value={values.userId}
                      setFieldValue={setFieldValue}
                      error={touched.userId && Boolean(errors.userId)}
                      helperText={touched.userId && errors.userId}
                    />
                  </Grid>



                  <Grid item xs={4}>
                    <TextField
                      value={values.phone}
                      style={{ marginTop: 0 }}
                      margin="dense"
                      label={i18n.t('plantao.modal.form.phone')}
                      placeholder="5513912344321"
                      variant="outlined" fullWidth
                      onChange={(e) => setFieldValue('phone', e.target.value)}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                    />

                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      value={values.interval.toString()}
                      style={{ marginTop: 0 }}
                      margin="dense"
                      label={i18n.t('plantao.modal.form.interval')}
                      placeholder=""
                      variant="outlined" fullWidth
                      onChange={(e) => setFieldValue('interval', e.target.value)}
                      error={touched.interval && Boolean(errors.interval)}
                      helperText={touched.interval ? errors.interval : i18n.t('plantao.modal.form.helperTextInterval')}
                    />

                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom> {i18n.t('plantao.modal.form.daysOfWeek')}</Typography>
                  </Grid>

                  {/* Exibir erro global do array days */}


                  {values.days.map((item, index) => {

                    const startTimeError = errors?.days?.[index]?.startTime;
                    const endTimeError = errors?.days?.[index]?.endTime;
                    const startTimeTouched = touched?.days?.[index]?.startTime;
                    const endTimeTouched = touched?.days?.[index]?.endTime;

                    return (
                      <Grid container spacing={1} key={index}>

                        <Grid item xs={4}>
                          <FastField
                            as={TextField}
                            label={i18n.t('plantao.modal.form.dayOfWeek')}
                            name={`days[${index}].weekday`}
                            disabled
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>

                        <Grid item xs={4}>
                          <FastField name={`days[${index}].startTime`}>
                            {({ field }) => (
                              <NumberFormat
                                label={i18n.t('plantao.modal.form.startOnCall')}
                                {...field}
                                variant="outlined"
                                margin="dense"
                                customInput={TextField}
                                format="##:##"
                                error={startTimeTouched && Boolean(startTimeError)}
                                helperText={startTimeTouched && startTimeError}
                              />
                            )}
                          </FastField>
                        </Grid>

                        <Grid item xs={4}>
                          <FastField name={`days[${index}].endTime`}>
                            {({ field }) => (
                              <NumberFormat
                                label={i18n.t('plantao.modal.form.endOnCall')}
                                {...field}
                                variant="outlined"
                                margin="dense"
                                customInput={TextField}
                                format="##:##"
                                error={endTimeTouched && Boolean(endTimeError)}
                                helperText={endTimeTouched && endTimeError}

                              />
                            )}
                          </FastField>
                        </Grid>
                      </Grid>
                    );
                  })}

                  {errors.days && typeof errors.days === 'string' && (
                    <Grid item xs={12}>
                      <Typography color="error" variant="body2">
                        {errors.days}
                      </Typography>
                    </Grid>
                  )}


                </Grid>


              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("userModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                >
                  {plantaoId
                    ? `${i18n.t("userModal.buttons.okEdit")}`
                    : `${i18n.t("userModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>

  );
}

export const UsersContainer = (props) => {

  const { value, setFieldValue, error, helperText } = props;

  const [listUsersAttendance, setListUsersAttendance] = React.useState([]);

  useEffect(() => {
    fetchUsersAttendance();
  }, [])

  const fetchUsersAttendance = async () => {

    try {
      const { data } = await api.get(`/users/list`);

      setListUsersAttendance(data);

    } catch (error) {
      console.log('erro buscar usuarios', error);
    }

  };

  const handleChange = (event) => {

    const { value: selectedUser } = event.target;

    setFieldValue('userId', selectedUser);
  };


  return (
    <FormControl
      error={error}
      helperText={helperText}
      fullWidth size="small" variant="outlined" >
      <InputLabel  >{i18n.t('plantao.modal.form.user')}</InputLabel>
      <Select
        variant="outlined"
        value={value}
        label={i18n.t('plantao.modal.form.user')}
        onChange={handleChange}
        MenuProps={MenuProps}

      >
        {listUsersAttendance.map((user) => (
          <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  )

}



const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 200,
    },
  },
};